import { gql, useApolloClient, useSubscription } from '@apollo/client';
import type { Socket } from 'phoenix';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import localLogout, {
  shouldIgnoreLocalLogout,
} from '../../account/localLogout';
import VersionMismatchModal from '../../error/VersionMismatchModal';
import type {
  CurrentSessionQueryVariables,
  CurrentVersionQuery,
  SessionLogoutSubscription,
} from '../../graphql/generated/graphqlTypes';
import useAutoShowInvitationModalEffect from '../../organization/useAutoShowInvitationModalEffect';
import UIActions from '../../ui/UIActions';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import { PreviousRouteProvider } from '../../utils/router/usePreviousRoute';
import useShortcut from '../../utils/useShortcut';
import useAppSelector from '../redux/useAppSelector';

const { __slab } = window;

const CURRENT_VERSION_QUERY = gql`
  query currentVersion {
    currentVersion {
      version
    }
  }
`;

const SESSION_LOGOUT_SUBSCRIPTION = gql`
  subscription sessionLogout {
    sessionLogout
  }
`;

const AppRoot = () => {
  const [topicId] = useSlug('topicSlug');
  const navigate = useNavigate();
  useShortcut('shift+n', (e) => {
    e.preventDefault();
    navigate(createPostPath({ topicId: topicId || undefined }));
  });

  const dispatch = useDispatch();
  const isLoggedIn = useAppSelector((state) => !!state.session.user.id);
  const socket = useAppSelector((state) => state.session.socket);

  const client = useApolloClient();

  const checkVersion = useMemo(
    () => () => {
      client
        .query<CurrentVersionQuery, CurrentSessionQueryVariables>({
          query: CURRENT_VERSION_QUERY,
          fetchPolicy: 'network-only',
        })
        .then(
          ({
            data: {
              currentVersion: { version },
            },
          }) => {
            const [serverMajor, serverMinor] = version.split('.');
            const [clientMajor, clientMinor] = __slab.VERSION.split('.');

            // Tab bar page shouldn't display version mismatch modal.
            if (
              window.location.pathname !== '/desktop/tabbar' &&
              (serverMinor !== clientMinor || serverMajor !== clientMajor)
            ) {
              dispatch(
                UIActions.openModal(VersionMismatchModal, {
                  close: () => {}, // Prevent user from closing
                }),
              );
            }
          },
        )
        .catch(() => {});
    },
    [client, dispatch],
  );

  useSubscription<SessionLogoutSubscription>(SESSION_LOGOUT_SUBSCRIPTION, {
    skip: !isLoggedIn,
    onSubscriptionData() {
      if (!shouldIgnoreLocalLogout()) {
        localLogout();
      }
    },
  });

  const prevSocketRef = useRef<Socket>();

  useEffect(() => {
    if (socket) {
      socket.onOpen(checkVersion);
    }

    if (prevSocketRef.current == null && socket != null) {
      checkVersion();
    }

    prevSocketRef.current = socket;
  }, [checkVersion, socket]);

  useAutoShowInvitationModalEffect();

  return (
    <PreviousRouteProvider>
      <Outlet />
    </PreviousRouteProvider>
  );
};

export default () => <AppRoot />;
